"use client";

import React, { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Clock, Phone, Mail, MessageSquare, StickyNote, UserPlus, RefreshCw, Send, Loader2, Trash2, User, Calendar, Shield, Eye, EyeOff, ChevronDown, Pencil, X, Save } from "lucide-react";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/_module/components/ui/popover";
import { useGetLeadAudits, useGetLeadActivity, useCreateLeadNote, useGetLeadNotes, useDeleteLeadNote, useUpdateLeadNote, useRevealLeadNote } from "../hooks/queries";
import { RichNoteEditor } from "./RichNoteEditor";
import { noteIsHtml, sanitizeNoteHtml, noteToPlainText, noteImageSources } from "./noteHtml";
import { uploadNoteImage } from "./noteImageUpload";
import { NoteImageLightbox, noteImageFromClick } from "./NoteImageLightbox";
import { useToast } from "@/app/_module/hooks/use-toast";

interface LeadActivityTimelineProps {
  leadId: string | undefined;
}

const actionDisplayNames: Record<string, string> = {
  lead_created: "Lead created",
  lead_updated: "Lead updated",
  lead_deleted: "Lead deleted",
  lead_soft_deleted: "Lead soft deleted",
  lead_permanently_deleted: "Lead permanently deleted",
  lead_restored: "Lead restored",
  lead_assigned: "Lead assigned",
  lead_unassigned: "Lead unassigned",
  lead_note_created: "Note added",
  lead_note_updated: "Note updated",
  lead_note_deleted: "Note deleted",
};

// Map field names to their section names and display names
const fieldConfig: Record<string, { section: string; displayName: string } | null> = {
  // General Information
  name: { section: 'General Information', displayName: 'Name' },
  firstName: { section: 'General Information', displayName: 'First Name' },
  lastName: { section: 'General Information', displayName: 'Last Name' },
  email: { section: 'General Information', displayName: 'Email' },
  phone: { section: 'General Information', displayName: 'Phone' },
  dob: { section: 'General Information', displayName: 'Date of Birth' },
  birthday: { section: 'General Information', displayName: 'Birthday' },
  age: { section: 'General Information', displayName: 'Age' },
  gender: { section: 'General Information', displayName: 'Gender' },
  address: { section: 'General Information', displayName: 'Address' },
  city: { section: 'General Information', displayName: 'City' },
  state: { section: 'General Information', displayName: 'State' },
  zipCode: { section: 'General Information', displayName: 'Zip Code' },
  license: { section: 'General Information', displayName: 'License' },

  // Family Information
  marital: { section: 'Family Information', displayName: 'Marital Status' },
  maritalStatus: { section: 'Family Information', displayName: 'Marital Status' },
  relationship: { section: 'Family Information', displayName: 'Relationship' },
  percentage: { section: 'Family Information', displayName: 'Percentage' },
  householdId: { section: 'Family Information', displayName: 'Household' },

  // Income Information
  employmentStatus: { section: 'Income Information', displayName: 'Employment Status' },
  earned: { section: 'Income Information', displayName: 'Earned Income' },
  unearned: { section: 'Income Information', displayName: 'Unearned Income' },
  worth: { section: 'Income Information', displayName: 'Net Worth' },
  annualIncome: { section: 'Income Information', displayName: 'Annual Income' },
  occupation: { section: 'Income Information', displayName: 'Occupation' },

  // Health Information
  height: { section: 'Health Information', displayName: 'Height' },
  weight: { section: 'Health Information', displayName: 'Weight' },
  tobaccoUse: { section: 'Health Information', displayName: 'Tobacco Use' },
  smokingHistory: { section: 'Health Information', displayName: 'Smoking History' },
  healthConditions: { section: 'Health Information', displayName: 'Health Conditions' },
  medications: { section: 'Health Information', displayName: 'Medications' },
  bloodPressure: { section: 'Health Information', displayName: 'Blood Pressure' },
  cholesterol: { section: 'Health Information', displayName: 'Cholesterol' },
  familyMedicalHistory: { section: 'Health Information', displayName: 'Family Medical History' },
  hobbies: { section: 'Health Information', displayName: 'Hobbies' },
  travelFrequency: { section: 'Health Information', displayName: 'Travel Frequency' },

  // Custom Fields / More Information
  customFields: { section: 'More Information', displayName: 'Custom Fields' },

  // Lead Management / Assignment
  leadStat: { section: 'Lead Management', displayName: 'Status' },
  status: { section: 'Lead Management', displayName: 'Status' },
  assigned: { section: 'Lead Management', displayName: 'Assigned To' },
  assignedToUserId: { section: 'Lead Management', displayName: 'Assigned User' },
  assignedByName: { section: 'Lead Management', displayName: 'Assigned By' },
  assignedToAgencyId: { section: 'Lead Management', displayName: 'Assigned Agency' },
  listName: { section: 'Lead Management', displayName: 'Import List' },

  // Appointment
  appointmentDate: { section: 'Appointment', displayName: 'Date' },
  appointmentTime: { section: 'Appointment', displayName: 'Time' },
  appointmentNotes: { section: 'Appointment', displayName: 'Notes' },

  // Policy Information
  coverageAmount: { section: 'Policy Information', displayName: 'Coverage Amount' },
  premiumAmount: { section: 'Policy Information', displayName: 'Premium Amount' },
  policyType: { section: 'Policy Information', displayName: 'Policy Type' },
  existingPolicies: { section: 'Policy Information', displayName: 'Existing Policies' },

  // Beneficiaries
  beneficiaries: { section: 'Beneficiaries', displayName: 'Beneficiaries' },

  // Notes
  notes: { section: 'Notes', displayName: 'Notes' },

  // Call History
  lastCall: { section: 'Call History', displayName: 'Last Call' },
  longestCall: { section: 'Call History', displayName: 'Longest Call' },
  lastCallAt: { section: 'Call History', displayName: 'Last Call Date' },
  lastLongestCallAt: { section: 'Call History', displayName: 'Longest Call Date' },

  // Verification
  emailVerifiedAt: { section: 'Verification', displayName: 'Email Verified' },
  phoneVerifiedAt: { section: 'Verification', displayName: 'Phone Verified' },

  // Underwriting
  uwrDate: { section: 'Underwriting', displayName: 'Underwriting Date' },

  // Other/System fields to exclude from display
  updatedAt: null,
  createdAt: null,
  deletedAt: null,
  id: null,
  leadId: null,
  userId: null,
  agencyId: null,
};

// Deep comparison for objects (customFields, etc.)
const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return a === b;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(key => deepEqual(a[key], b[key]));
};

// Prettify a tag for display: snake_case user tags become Title Case, the
// automatic status-history tags ("Was: Sold & Approved") keep their exact label.
const prettyTag = (t: string) =>
  t.startsWith('Was: ') ? t : t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Get changes grouped by section with field names
const getChangedSectionsWithFields = (oldValues: any, newValues: any): string[] => {
  if (!oldValues || !newValues) return [];

  // Group changed fields by section
  const sectionFields: Record<string, string[]> = {};
  const allKeys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);

  // Tags get a rich diff instead of a bare field name: show exactly which
  // tags were added/removed ("Tags: added Callback · removed Hot Lead").
  let tagsDetail: string | null = null;

  allKeys.forEach(key => {
    if (key === 'tags') {
      const oldArr: string[] = Array.isArray(oldValues.tags) ? oldValues.tags : [];
      const newArr: string[] = Array.isArray(newValues.tags) ? newValues.tags : [];
      const added = newArr.filter(t => !oldArr.includes(t));
      const removed = oldArr.filter(t => !newArr.includes(t));
      if (added.length || removed.length) {
        const parts: string[] = [];
        if (added.length) parts.push(`added ${added.map(prettyTag).map(t => `“${t}”`).join(', ')}`);
        if (removed.length) parts.push(`removed ${removed.map(prettyTag).map(t => `“${t}”`).join(', ')}`);
        tagsDetail = `Tags: ${parts.join(' · ')}`;
      }
      return;
    }
    // Use deep comparison for objects like customFields
    const oldVal = oldValues[key];
    const newVal = newValues[key];
    const isChanged = typeof oldVal === 'object' && oldVal !== null
      ? !deepEqual(oldVal, newVal)
      : oldVal !== newVal;

    if (isChanged) {
      const config = fieldConfig[key];
      // Only add if config exists and is not null (excludes system fields)
      if (config) {
        if (!sectionFields[config.section]) {
          sectionFields[config.section] = [];
        }
        sectionFields[config.section].push(config.displayName);
      }
    }
  });

  // Format as "Section (Field1, Field2)"
  const formatted = Object.entries(sectionFields).map(([section, fields]) => {
    if (fields.length === 1) {
      return `${section} (${fields[0]})`;
    }
    return `${section} (${fields.join(', ')})`;
  });
  if (tagsDetail) formatted.push(tagsDetail);
  return formatted;
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

// Format call status in activity descriptions (e.g., "Outbound call - no_answer" → "Outbound call - No Answer")
const formatActivityDescription = (description: string): string => {
  const statusMap: Record<string, string> = {
    'no_answer': 'No Answer',
    'no-answer': 'No Answer',
    'no_answered': 'No Answer',
    'completed': 'Completed',
    'answered': 'Answered',
    'busy': 'Busy',
    'failed': 'Failed',
    'canceled': 'Canceled',
    'missed': 'Missed',
    'declined': 'Declined',
    'rejected': 'Rejected',
    'initiated': 'Initiated',
    'in-progress': 'In Progress',
    'in_progress': 'In Progress',
    'ringing': 'Ringing',
    'queued': 'Queued',
  };

  // Match "call - <status>" pattern and replace the status portion
  return description.replace(/call\s*-\s*(\S+)$/i, (match, status) => {
    const formatted = statusMap[status.toLowerCase()];
    return formatted ? `call - ${formatted}` : match;
  });
};

const getActivityIcon = (type: string) => {
  switch (type) {
    case "call_made":
      return <Phone className="h-3 w-3 text-primary" />;
    case "email_sent":
      return <Mail className="h-3 w-3 text-orange-600" />;
    case "sms_sent":
      return <MessageSquare className="h-3 w-3 text-cyan-600" />;
    case "note_added":
      return <StickyNote className="h-3 w-3 text-yellow-600" />;
    case "lead_assigned":
      return <UserPlus className="h-3 w-3 text-primary" />;
    case "status_changed":
    case "lead_updated":
      return <RefreshCw className="h-3 w-3 text-green-600" />;
    default:
      return <div className="w-2 h-2 bg-primary rounded-full" />;
  }
};

const NOTE_CATEGORY_COLORS: Record<string, string> = {
  general: "bg-gray-100 text-gray-700",
  follow_up: "bg-primary/10 text-primary",
  important: "bg-red-100 text-red-700",
  quote: "bg-green-100 text-green-700",
  call: "bg-primary/10 text-primary",
  meeting: "bg-orange-100 text-orange-700",
};

const NOTE_CATEGORY_LABELS: Record<string, string> = {
  general: "General",
  follow_up: "Follow-up",
  important: "Important",
  quote: "Quote",
  call: "Call",
  meeting: "Meeting",
};

export const LeadActivityTimeline = function({ leadId }: LeadActivityTimelineProps) {
  const { data: audits = [], isLoading: auditsLoading, error: auditsError } = useGetLeadAudits(leadId || '');
  const { data: activities = [], isLoading: activitiesLoading, error: activitiesError } = useGetLeadActivity(leadId || '');
  const timelineQueryClient = useQueryClient();

  // Live update: when a call ends anywhere in the app, refetch this lead's
  // activity immediately AND again 8s later — the call_histories row for
  // canceled/no-answer calls is created by Twilio's status webhook a few
  // seconds AFTER the call ends, so a single immediate refetch would miss it.
  // (The 15s refetchInterval on the query is the safety net for webhook-only
  // events like inbound SMS.)
  React.useEffect(() => {
    if (!leadId) return;
    let delayed: ReturnType<typeof setTimeout> | null = null;
    const onCallEnded = () => {
      timelineQueryClient.invalidateQueries({ queryKey: ["lead-activity", leadId] });
      timelineQueryClient.invalidateQueries({ queryKey: ["lead-audits", leadId] });
      if (delayed) clearTimeout(delayed);
      delayed = setTimeout(() => {
        timelineQueryClient.invalidateQueries({ queryKey: ["lead-activity", leadId] });
      }, 8000);
    };
    window.addEventListener('dialer:call-ended', onCallEnded);
    return () => {
      window.removeEventListener('dialer:call-ended', onCallEnded);
      if (delayed) clearTimeout(delayed);
    };
  }, [leadId, timelineQueryClient]);
  const { data: notesResponse, isLoading: notesLoading } = useGetLeadNotes(leadId || '');
  const { mutateAsync: createNote, isPending: isCreatingNote } = useCreateLeadNote();
  const { mutateAsync: updateNote, isPending: isUpdatingNote } = useUpdateLeadNote();

  // ── Expandable + editable note activities ────────────────────────────────
  // Click a note activity -> expands to the full note; Edit opens the rich
  // editor inline; Delete removes it. Sensitive notes must be revealed first
  // (editing the masked copy would overwrite the real content with asterisks).
  const [expandedNoteIds, setExpandedNoteIds] = useState<Set<string>>(new Set());
  const [noteLightboxSrc, setNoteLightboxSrc] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  // Notes-popover inline edit (separate from expanded-row edit state)
  const [popEditNoteId, setPopEditNoteId] = useState<string | null>(null);
  const [popEditHtml, setPopEditHtml] = useState("");

  const savePopoverEdit = async (note: any) => {
    if (!popEditHtml.trim() || !leadId || isUpdatingNote) return;
    try {
      await updateNote({ leadId, noteId: note.id, params: {
        note: popEditHtml,
        category: note.category || "general",
        priority: note.priority || "medium",
      }});
      setPopEditNoteId(null);
    } catch { /* hook surfaces errors */ }
  };
  const [editHtml, setEditHtml] = useState("");
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState<string | null>(null);
  const toggleNoteExpanded = (noteId: string) => {
    setExpandedNoteIds(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId); else next.add(noteId);
      return next;
    });
    setConfirmDeleteNoteId(null);
  };
  // Full note lookup from the notes list (activity items only carry a preview)
  const findFullNote = (noteId: string | null) =>
    noteId ? (notes as any[]).find((n: any) => n.id === noteId) : undefined;

  // Collapsed-row thumbnails: image-only notes previously previewed as
  // nothing ("Added note:" with a blank line). Show mini thumbnails inline;
  // clicking one opens the lightbox, clicking the row still expands.
  const renderCollapsedNoteThumbs = (html: string | null | undefined) => {
    const srcs = noteImageSources(html);
    if (!srcs.length) return null;
    return (
      <div className="mt-1.5 flex items-center gap-1.5">
        {srcs.slice(0, 4).map((src, i) => (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={i}
            src={src}
            alt=""
            className="h-12 w-auto max-w-[110px] rounded border border-gray-200 object-cover cursor-zoom-in"
            onClick={(e) => { e.stopPropagation(); setNoteLightboxSrc(src); }}
          />
        ))}
        {srcs.length > 4 && <span className="text-[10px] text-gray-400">+{srcs.length - 4} more</span>}
      </div>
    );
  };

  const renderExpandedNote = (noteId: string, fallbackText?: string | null) => {
    const full = findFullNote(noteId);
    const sensitive = !!(full?.hasSensitiveData);
    const revealed = revealedNotes[noteId];
    const body = (sensitive && revealed) ? revealed : (full?.note ?? fallbackText ?? "");
    const isEditing = editingNoteId === noteId;
    const canEdit = !!full && (!sensitive || !!revealed);

    const saveInlineEdit = async () => {
      if (!editHtml.trim() || isUpdatingNote) return;
      try {
        await updateNote({ leadId: leadId!, noteId, params: {
          note: editHtml,
          category: full?.category || "general",
          priority: full?.priority || "medium",
        }});
        setEditingNoteId(null);
      } catch { /* hook surfaces errors */ }
    };

    return (
      <div className="mt-2 rounded-md border border-gray-200 bg-white p-3 space-y-2" data-rich-note-root onClick={(e) => e.stopPropagation()}>
        {isEditing ? (
          <>
            <RichNoteEditor
              initialContent={body}
              minHeight="180px"
              placeholder="Edit note…"
              onChange={(html) => setEditHtml(html)}
              onImageUpload={leadId ? (file) => uploadNoteImage(leadId, file) : undefined}
              onAutoSave={() => {
                // Click-away: save changes, or just close if nothing changed
                if (editHtml.trim() && editHtml !== body) void saveInlineEdit();
                else setEditingNoteId(null);
              }}
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="h-7"
                disabled={!editHtml.trim() || isUpdatingNote}
                onClick={saveInlineEdit}
              >
                {isUpdatingNote ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                Save
              </Button>
              <Button size="sm" variant="outline" className="h-7" onClick={() => setEditingNoteId(null)}>
                <X className="h-3 w-3 mr-1" /> Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            {noteIsHtml(body) ? (
              <div
                className="text-sm text-gray-900 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600 [&_blockquote]:italic [&_hr]:my-2 [&_hr]:border-gray-200 [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:rounded-md [&_img]:border [&_img]:border-gray-200 [&_img]:my-1.5 [&_img]:cursor-zoom-in"
                onClick={(e) => { const src = noteImageFromClick(e); if (src) setNoteLightboxSrc(src); }}
                dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(body) }}
              />
            ) : (
              <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">{body}</p>
            )}
            <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-gray-600"
                disabled={!canEdit}
                title={sensitive && !revealed ? "Reveal the note first to edit it" : undefined}
                onClick={() => { setEditHtml(body); setEditingNoteId(noteId); }}
              >
                <Pencil className="h-3 w-3 mr-1" /> Edit
              </Button>
              {confirmDeleteNoteId === noteId ? (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-gray-500">Delete this note?</span>
                  <Button size="sm" variant="destructive" className="h-6 px-2 text-xs"
                    onClick={async () => {
                      try { await deleteNote({ leadId: leadId!, noteId }); } catch { /* surfaced */ }
                      setConfirmDeleteNoteId(null);
                    }}>
                    Yes, delete
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setConfirmDeleteNoteId(null)}>
                    Keep
                  </Button>
                </span>
              ) : (
                <Button size="sm" variant="ghost" className="h-7 text-rose-500 hover:text-rose-700"
                  onClick={() => setConfirmDeleteNoteId(noteId)}>
                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                </Button>
              )}
              {sensitive && !revealed && (
                <span className="text-[10px] text-amber-600 ml-auto">Reveal to see/edit the original</span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };
  const { mutateAsync: deleteNote, isPending: isDeletingNote } = useDeleteLeadNote();
  const { mutateAsync: revealNote } = useRevealLeadNote();
  const { toast } = useToast();

  const [notesOpen, setNotesOpen] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [newNoteMentions, setNewNoteMentions] = useState<any[]>([]);
  const [composerKey, setComposerKey] = useState(0);
  const [revealedNotes, setRevealedNotes] = useState<Record<string, string>>({});
  const [revealingNoteId, setRevealingNoteId] = useState<string | null>(null);

  // Extract and sort notes
  const notes = useMemo(() => {
    if (!notesResponse) return [];
    const list = Array.isArray(notesResponse) ? notesResponse : (notesResponse as any)?.data || [];
    return [...list].sort(
      (a: any, b: any) => new Date(b.createdAt || b.created_at).getTime() - new Date(a.createdAt || a.created_at).getTime()
    );
  }, [notesResponse]);

  const handleCreateNote = async () => {
    if (!newNoteText.trim() || !leadId) return;
    try {
      await createNote({
        leadId,
        params: {
          note: newNoteText.trim(),
          category: "general",
          priority: "medium",
          ...(newNoteMentions.length > 0 ? { mentions: newNoteMentions } : {}),
        } as any,
      });
      setNewNoteText("");
      setNewNoteMentions([]);
      setComposerKey((k) => k + 1); // clears the rich editor
      toast({ title: "Note added", description: "Your note has been saved." });
    } catch {
      toast({ title: "Failed to add note", variant: "destructive" });
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!leadId) return;
    try {
      await deleteNote({ leadId, noteId });
      toast({ title: "Note deleted" });
    } catch {
      toast({ title: "Failed to delete note", variant: "destructive" });
    }
  };

  const handleNoteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCreateNote();
    }
  };

  const handleToggleReveal = async (noteId: string) => {
    if (revealedNotes[noteId]) {
      setRevealedNotes((prev) => {
        const next = { ...prev };
        delete next[noteId];
        return next;
      });
      return;
    }
    if (!leadId) return;
    setRevealingNoteId(noteId);
    try {
      const data = await revealNote({ leadId, noteId });
      const text = typeof data === "string" ? data : (data as any)?.data || (data as any)?.note || "";
      setRevealedNotes((prev) => ({ ...prev, [noteId]: text }));
    } catch {
      toast({ title: "Failed to reveal note", variant: "destructive" });
    } finally {
      setRevealingNoteId(null);
    }
  };

  const isLoading = auditsLoading || activitiesLoading;
  const error = auditsError || activitiesError;

  // Merge audits and activities, then sort by timestamp.
  // Also group consecutive "Inbound call - No Answer" entries from the same
  // lead within a 2-minute window into one item — customers often redial 2-3
  // times in quick succession (hang up → redial within seconds) which agents
  // previously perceived as a duplication bug. We show a "×N" badge instead.
  const combinedActivity = React.useMemo(() => {
    const auditItems = audits.map((audit: any) => ({
      ...audit,
      sourceType: 'audit',
      timestamp: audit.createdAt,
    }));

    const activityItems = activities.map((activity: any) => ({
      ...activity,
      sourceType: 'activity',
    }));

    const sorted = [...auditItems, ...activityItems].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    // Group consecutive inbound-no-answer entries that happen within 60s of
    // each other. 60s catches iPhone/carrier auto-retry (3–15s) and impatient
    // redials (10–30s) but keeps genuinely separate call attempts (>1 min
    // apart) as distinct entries. Prod test: customer called 2x around 18:29
    // and 2x around 18:30–31 — each pair's gap <30s, between-pair gap 109s,
    // so they correctly render as 2 groups of ×2 redials, not 1 group of ×4.
    const REDIAL_WINDOW_MS = 60 * 1000;
    const isInboundNoAnswer = (item: any): boolean => {
      if (item?.sourceType !== 'activity') return false;
      if (item?.type !== 'call_made') return false;
      const desc = String(item?.description || '').toLowerCase();
      return desc.includes('inbound call') && (desc.includes('no answer') || desc.includes('no_answer'));
    };

    const grouped: any[] = [];
    for (const item of sorted) {
      const last = grouped[grouped.length - 1];
      if (
        last &&
        isInboundNoAnswer(item) &&
        isInboundNoAnswer(last) &&
        Math.abs(new Date(last.timestamp).getTime() - new Date(item.timestamp).getTime()) <= REDIAL_WINDOW_MS
      ) {
        last.redialCount = (last.redialCount || 1) + 1;
        last.earliestTimestamp = item.timestamp; // oldest in the group
        continue;
      }
      grouped.push({ ...item });
    }
    return grouped;
  }, [audits, activities]);

  if (isLoading) {
    return (
      <Card className="shadow-sm border-gray-200">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg animate-pulse">
                <div className="w-2 h-2 bg-gray-300 rounded-full mt-2" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm border-gray-200">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="text-sm text-red-600">
            Failed to load activity history
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!combinedActivity || combinedActivity.length === 0) {
    return (
      <Card className="shadow-sm border-gray-200">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="flex items-center justify-center py-8 text-gray-500">
            <div className="text-center">
              <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No activity recorded yet</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-gray-200">
      {noteLightboxSrc && <NoteImageLightbox src={noteLightboxSrc} onClose={() => setNoteLightboxSrc(null)} />}
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Recent Activity
          </h3>
          <Popover open={notesOpen} onOpenChange={setNotesOpen}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
              >
                <StickyNote className="h-4 w-4" />
                <span className="text-xs font-medium">Notes</span>
                {notes.length > 0 && (
                  <Badge variant="secondary" className="h-5 min-w-[20px] px-1.5 text-[10px] bg-yellow-100 text-yellow-700">
                    {notes.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[520px] max-w-[95vw] p-0" sideOffset={8}>
              <div className="p-3 border-b border-gray-100">
                <h4 className="text-sm font-semibold text-gray-900">Lead Notes</h4>
              </div>
              {/* Rich composer — same editor as the Notes tab, compact */}
              <div className="p-3 border-b border-gray-100 bg-gray-50 space-y-2">
                <RichNoteEditor
                  key={composerKey}
                  minHeight="110px"
                  placeholder="Write a note… formatting + @mentions work here too"
                  onChange={(html, _text, mentions) => {
                    setNewNoteText(html);
                    setNewNoteMentions(mentions);
                  }}
                  onImageUpload={leadId ? (file) => uploadNoteImage(leadId, file) : undefined}
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={handleCreateNote}
                    disabled={!newNoteText.trim() || isCreatingNote}
                  >
                    {isCreatingNote ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-1" />
                    )}
                    Save note
                  </Button>
                </div>
              </div>
              {/* Notes list */}
              <div className="max-h-80 overflow-y-auto">
                {notesLoading ? (
                  <div className="p-6 text-center text-sm text-gray-400">Loading notes...</div>
                ) : notes.length === 0 ? (
                  <div className="p-6 text-center">
                    <StickyNote className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-400">No notes yet</p>
                  </div>
                ) : (
                  notes.map((note: any) => {
                    const ts = note.createdAt || note.created_at;
                    const catColor = NOTE_CATEGORY_COLORS[note.category] || NOTE_CATEGORY_COLORS.general;
                    const catLabel = NOTE_CATEGORY_LABELS[note.category] || note.category;
                    return (
                      <div key={note.id} className="p-3 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 group" data-rich-note-root={popEditNoteId === note.id ? true : undefined}>
                        {popEditNoteId === note.id ? (
                          <div className="space-y-2">
                            <RichNoteEditor
                              initialContent={revealedNotes[note.id] || note.note}
                              minHeight="160px"
                              placeholder="Edit note…"
                              onChange={(html) => setPopEditHtml(html)}
                              onImageUpload={leadId ? (file) => uploadNoteImage(leadId, file) : undefined}
                              onAutoSave={() => {
                                const original = revealedNotes[note.id] || note.note;
                                if (popEditHtml.trim() && popEditHtml !== original) void savePopoverEdit(note);
                                else setPopEditNoteId(null);
                              }}
                            />
                            <div className="flex items-center gap-2">
                              <Button size="sm" className="h-7" disabled={!popEditHtml.trim() || isUpdatingNote} onClick={() => savePopoverEdit(note)}>
                                {isUpdatingNote ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />} Save
                              </Button>
                              <Button size="sm" variant="outline" className="h-7" onClick={() => setPopEditNoteId(null)}>
                                <X className="h-3 w-3 mr-1" /> Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                        <div className="flex items-start justify-between gap-2">
                          {(() => {
                            const body = revealedNotes[note.id] || note.note;
                            return noteIsHtml(body) ? (
                              <div
                                className="text-sm text-gray-900 leading-relaxed flex-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600 [&_blockquote]:italic [&_hr]:my-2 [&_hr]:border-gray-200 [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:rounded-md [&_img]:border [&_img]:border-gray-200 [&_img]:my-1.5 [&_img]:cursor-zoom-in"
                                onClick={(e) => { const src = noteImageFromClick(e); if (src) setNoteLightboxSrc(src); }}
                                dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(body) }}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap flex-1">{body}</p>
                            );
                          })()}
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            {note.hasSensitiveData && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-amber-500 hover:text-amber-700"
                                onClick={() => handleToggleReveal(note.id)}
                                disabled={revealingNoteId === note.id}
                              >
                                {revealingNoteId === note.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : revealedNotes[note.id] ? (
                                  <EyeOff className="h-3 w-3" />
                                ) : (
                                  <Eye className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary"
                              title={note.hasSensitiveData && !revealedNotes[note.id] ? "Reveal the note before editing (editing the masked copy would overwrite the real content)" : "Edit note"}
                              disabled={note.hasSensitiveData && !revealedNotes[note.id]}
                              onClick={() => {
                                setPopEditNoteId(note.id);
                                setPopEditHtml(revealedNotes[note.id] || note.note);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600"
                              onClick={() => handleDeleteNote(note.id)}
                              disabled={isDeletingNote}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${catColor}`}>
                            {catLabel}
                          </Badge>
                          {note.hasSensitiveData && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700">
                              <Shield className="h-2.5 w-2.5 mr-0.5" />
                              Sensitive
                            </Badge>
                          )}
                          <span className="text-[11px] text-gray-400 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {note.commentedByName || note.createdByName || "Unknown"}
                          </span>
                          <span className="text-[11px] text-gray-400 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {ts ? formatDateTime(ts) : ""}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {combinedActivity.map((item: any) => {
            // Render communication activity items (calls, emails, SMS, notes)
            if (item.sourceType === 'activity') {
              const isNoteActivity = item.type === 'note_added';
              const activityNoteId = isNoteActivity ? item.noteId : null;
              const activityHasSensitive = isNoteActivity && item.hasSensitiveData;

              return (
                <div
                  key={item.id}
                  className={`flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors ${activityNoteId ? "cursor-pointer" : ""}`}
                  onClick={activityNoteId ? () => toggleNoteExpanded(activityNoteId) : undefined}
                >
                  <div className="flex-shrink-0 mt-1">
                    {getActivityIcon(item.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900 [&>*]:align-middle">
                        {activityNoteId && (
                          <ChevronDown className={`inline-block h-3.5 w-3.5 mr-1 text-gray-400 transition-transform ${expandedNoteIds.has(activityNoteId) ? "rotate-180" : ""}`} />
                        )}
                        {activityHasSensitive && activityNoteId && revealedNotes[activityNoteId]
                          ? `Added note: ${noteToPlainText(revealedNotes[activityNoteId]).substring(0, 50)}${noteToPlainText(revealedNotes[activityNoteId]).length > 50 ? '...' : ''}`
                          : formatActivityDescription(noteToPlainText(item.description))}
                        {item.redialCount > 1 && (
                          <Badge variant="secondary" className="inline-flex ml-1.5 h-4 px-1.5 text-[9px] font-medium bg-primary/10 text-primary border-0 whitespace-nowrap">
                            ×{item.redialCount} redials
                          </Badge>
                        )}
                        {item.type === 'call_made' && item.metadata?.dialerMode && (
                          <Badge
                            variant="secondary"
                            className={`inline-flex ml-1.5 h-4 border-0 px-1.5 text-[9px] font-medium whitespace-nowrap ${
                              item.metadata.dialerMode === 'power_dialer'
                                ? 'bg-violet-50 text-violet-700'
                                : item.metadata.dialerMode === 'multi_line'
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {item.metadata.dialerMode === 'power_dialer' ? 'Power Dialer' : item.metadata.dialerMode === 'multi_line' ? 'Multi-Line' : 'Manual'}
                          </Badge>
                        )}
                        {item.type === 'call_made' && (item.metadata as any)?.transferredTo && (
                          <Badge variant="secondary" className="inline-flex ml-1.5 h-4 border-0 px-1.5 text-[9px] font-medium whitespace-nowrap bg-emerald-50 text-emerald-700">
                            → {(item.metadata as any).transferredTo}
                          </Badge>
                        )}
                      </p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {activityHasSensitive && activityNoteId && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-amber-500 hover:text-amber-700"
                            onClick={(e) => { e.stopPropagation(); handleToggleReveal(activityNoteId); }}
                            disabled={revealingNoteId === activityNoteId}
                          >
                            {revealingNoteId === activityNoteId ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : revealedNotes[activityNoteId] ? (
                              <EyeOff className="h-3 w-3" />
                            ) : (
                              <Eye className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                        <p className="text-xs text-gray-500 whitespace-nowrap">
                          {formatDateTime(item.timestamp)}
                        </p>
                      </div>
                    </div>
                    {activityNoteId && !expandedNoteIds.has(activityNoteId) &&
                      renderCollapsedNoteThumbs(findFullNote(activityNoteId)?.note)}
                    <div className="flex items-center gap-2 mt-1">
                      {item.agentName && (() => {
                        // For inbound calls the agent didn't initiate the call —
                        // the customer did. Using "by {agent}" there is misleading
                        // ("by Bruce Wayne" on an inbound looks like Bruce called
                        // the lead). Show "to {agent}" for inbound (the call was
                        // routed TO the agent), and keep "by {agent}" for
                        // outbound/sms/notes where the agent is the actor.
                        const desc = String(item.description || '').toLowerCase();
                        const isInbound = item.type === 'call_made' && desc.includes('inbound call');
                        const clockTime = new Date(item.timestamp).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        });
                        const durSecs = item.type === 'call_made' ? Number(item.metadata?.duration) || 0 : 0;
                        const dur = durSecs > 0 ? `${Math.floor(durSecs / 60)}:${String(durSecs % 60).padStart(2, '0')}` : null;
                        return (
                          <p className="text-xs text-gray-600">
                            {isInbound ? 'to ' : 'by '}{item.agentName}
                            <span className="text-gray-400"> · {clockTime}</span>
                            {dur && <span className="text-gray-400"> · {dur} talk</span>}
                          </p>
                        );
                      })()}
                      {activityHasSensitive && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700">
                          <Shield className="h-2.5 w-2.5 mr-0.5" />
                          Sensitive
                        </Badge>
                      )}
                    </div>
                    {activityNoteId && expandedNoteIds.has(activityNoteId) && renderExpandedNote(activityNoteId)}
                  </div>
                </div>
              );
            }

            // Render audit log items
            const changedSections = getChangedSectionsWithFields(item.oldValues, item.newValues);
            const actionName = actionDisplayNames[item.action] || item.action;
            const isNoteAction = item.action === 'lead_note_created' || item.action === 'lead_note_updated';
            const noteText = isNoteAction ? (item.newValues?.note || item.newValues?.notes) : null;
            const noteId = isNoteAction ? (item.newValues?.id || item.entityId) : null;
            const noteHasSensitiveData = isNoteAction && item.newValues?.hasSensitiveData;

            return (
              <div
                key={item.id}
                className={`flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors ${isNoteAction && noteId ? "cursor-pointer" : ""}`}
                onClick={isNoteAction && noteId ? () => toggleNoteExpanded(noteId) : undefined}
              >
                <div className="flex-shrink-0 mt-1">
                  {getActivityIcon(item.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                      {isNoteAction && noteId && (
                        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${expandedNoteIds.has(noteId) ? "rotate-180" : ""}`} />
                      )}
                      {actionName}
                    </p>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {noteHasSensitiveData && noteId && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-amber-500 hover:text-amber-700"
                          onClick={(e) => { e.stopPropagation(); handleToggleReveal(noteId); }}
                          disabled={revealingNoteId === noteId}
                        >
                          {revealingNoteId === noteId ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : revealedNotes[noteId] ? (
                            <EyeOff className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                      <p className="text-xs text-gray-500 whitespace-nowrap">
                        {formatDateTime(item.createdAt)}
                      </p>
                    </div>
                  </div>
                  {noteText && (
                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap line-clamp-3">
                      {noteToPlainText((noteHasSensitiveData && noteId && revealedNotes[noteId]) ? revealedNotes[noteId] : noteText)}
                    </p>
                  )}
                  {isNoteAction && noteId && !expandedNoteIds.has(noteId) &&
                    renderCollapsedNoteThumbs((noteId && revealedNotes[noteId]) || findFullNote(noteId)?.note || noteText)}
                  {noteHasSensitiveData && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 mt-1 bg-amber-100 text-amber-700">
                      <Shield className="h-2.5 w-2.5 mr-0.5" />
                      Sensitive
                    </Badge>
                  )}
                  {isNoteAction && noteId && expandedNoteIds.has(noteId) && renderExpandedNote(noteId, noteText)}
                  {!isNoteAction && changedSections.length > 0 && (
                    <p className="text-xs text-gray-600 mt-1">
                      Updated: {changedSections.join(', ')}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default LeadActivityTimeline;
